import { useNavigation, useRoute } from '@react-navigation/native';
import { Headline3, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import AimoMarker from 'components/map/AimoMarker';
import AimoPolygon from 'components/map/AimoPolygon';
import MapComponent from 'components/map/MapComponent';
import ParkingSchedule from 'components/park/ParkingSchedule';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import ParkingContext from 'context/ParkingContext';
import useGetMatchingPermitsForParkingZone from 'hooks/useGetMatchingPermitsForParkingZone';
import useReadPurchasedPrepaidTickets from 'hooks/useReadPurchasedPrepaidTickets';
import { isArray } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, StyleSheet } from 'react-native';
import { useMapStore } from 'stores/mapStore';
import styled from 'styled-components/native';
import { Divider } from 'styles/CommonLayoutStyles';
import { ColumnWithMargins, ScreenContainer } from 'styles/ContainerStyles';
import { ParkingDetailsNavigationProp, ParkNavProps } from 'types/navigation';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { makePoint, openDirections } from 'utils/mapUtils';
import { getLocationIconType } from 'utils/parkUtils';
import ParkingLoyaltyPermits from './ParkingLoyaltyPermits';
import ParkingPrepaidTickets from './ParkingPrepaidTickets';
import { ImportantZoneNotes, OtherNotes } from './ParkingZoneNotes';
import ZoneDetailsRow from './ZoneDetailsRow';

const Container = styled.View<{ gap?: number }>`
  margin-top: 10px;
  padding: 0 20px;
  gap: ${({ gap }) => gap ?? 11}px;
`;

const ParkingZoneInfo = () => {
  const { parkingZoneResult: parkingZone, hasEVCharge } =
    useContext(ParkingContext);

  const uid = parkingZone?.data?.parkZone?.uid;
  const {
    area,
    address,
    locationType,
    parkingFacility,
    parkingMethods,
    name,
    countryCode,
  } = parkingZone?.data?.parkZone || {};
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();
  const {
    flags: { anpr },
  } = useContext(FeatureFlagsContext);
  const {
    groupedMatchingPermits: { LOYALTY: loyaltyPermits },
  } = useGetMatchingPermitsForParkingZone(uid, ['LOYALTY']);
  const { mySalesOrdersForZone } = useContext(ParkingContext);
  const { purchasedPrepaidTickets } = useReadPurchasedPrepaidTickets({});

  const purchasedTickets = [
    ...(mySalesOrdersForZone ?? []),
    ...purchasedPrepaidTickets,
  ];

  const { t } = useTranslation();
  const showCameraRecognition = showFeatureFlag(anpr);
  const markerType = getLocationIconType(
    locationType,
    showCameraRecognition && parkingMethods?.anpr
  );
  const [showTickets, setShowTickets] = useState(false);
  const [showLoyalty, setShowLoyalty] = useState(false);

  const mapCoords = useMapStore((state) => state.mapCoordinates);

  const marker = address?.marker;
  const markerCoordinate = marker && makePoint(marker?.coordinates);

  const { params } = useRoute<ParkingDetailsNavigationProp['route']>();
  const { parkingZoneUid } = params;

  const navigateToChargeOptions = () => {
    navigatePark('chargeOptions', {
      parkingZoneUid: parkingZoneUid || '',
    });
  };

  useEffect(() => {
    if (loyaltyPermits && loyaltyPermits.length > 0) {
      setShowLoyalty(true);
    }
    if (purchasedTickets && purchasedTickets.length > 0) {
      setShowTickets(true);
    }
  }, [loyaltyPermits, purchasedTickets]);

  const initialRegion = useMemo(() => {
    const isStreet = locationType === 'STREET' && countryCode === 'FI';
    if (isStreet) {
      const streetCords = isArray(mapCoords)
        ? mapCoords.find(Boolean)
        : mapCoords;
      if (streetCords) {
        return streetCords;
      }
    }
    return markerCoordinate;
  }, [locationType, countryCode, markerCoordinate, mapCoords]);

  return (
    <ScreenContainer mt={10} mb={0}>
      <Container>
        <ImportantZoneNotes />
        <ParkingSchedule parkingFacilityData={parkingFacility} />
        <MapComponent
          showBeforeMapLoaded={false}
          showLocation={false}
          showLogo={false}
          onPress={() => undefined}
          mapPadding={{ top: 0, left: 0, right: 0, bottom: 0 }}
          scrollEnabled
          focusOnUserLocation={false}
          mapStyle={styles.map}
          mapStyleWeb={styles.map}
          initialRegion={
            initialRegion
              ? {
                  ...initialRegion,
                  latitudeDelta: 0.001,
                  longitudeDelta: 0.002,
                }
              : undefined
          }>
          {markerCoordinate && (
            <AimoMarker
              key={`parking-spot-${parkingZoneUid}`}
              coordinate={markerCoordinate}
              markerType={markerType}
              activeIndex={1}
              onPress={() => undefined}
              testID={`${markerType}-marker-${parkingZoneUid}`}
              id={parkingZoneUid}
              isSelected
            />
          )}
          {area ? (
            <AimoPolygon
              onPress={() => undefined}
              geoJsonMultiPolygon={area}
              id={parkingZoneUid}
            />
          ) : undefined}
        </MapComponent>
        {(address?.street || initialRegion) && (
          <ZoneDetailsRow
            icon={
              <Icon
                name="Location-Filled-01"
                size="m"
                color={theme.colors.primary}
              />
            }
            title={address?.street ?? ''}
            linkText={t('parking.route')}
            onPress={() => openDirections(name, initialRegion)}
            chevronSize="m"
            chevronColor={theme.colors.primary}
            linkColor={theme.colors.primary}
            borderBottom={false}
            borderTop={true}
          />
        )}
      </Container>
      {(hasEVCharge || showTickets || showLoyalty) && <Divider size={15} />}
      <Container gap={0}>
        {(hasEVCharge || showTickets || showLoyalty) && (
          <ColumnWithMargins mb={11}>
            <Headline3>{t('parking.yourOptions')}</Headline3>
          </ColumnWithMargins>
        )}
        {hasEVCharge && (
          <ZoneDetailsRow
            title={t('button.chargingOptions')}
            icon={<Icon name="Charging" />}
            onPress={navigateToChargeOptions}
            chevronColor={theme.colors.black}
            borderTop
          />
        )}
        <ParkingPrepaidTickets showTicketsCallback={setShowTickets} />
        <ParkingLoyaltyPermits showLoyaltyCallback={setShowLoyalty} />
        <OtherNotes />
      </Container>
    </ScreenContainer>
  );
};

export default ParkingZoneInfo;

const styles = StyleSheet.create({
  map: {
    height: Dimensions.get('screen').height / 3,
  },
});
